import * as React from 'react';
import * as ReactDOM from 'react-dom';

import * as CertificationWatermark from '@tableau/certification-watermark';

import {
  ButtonType, ButtonWidget,
  DialogPositioningBehavior,
  DialogProps, DialogType,
  DialogWidget,
  getClientPosition,
  InteractionState,
} from '@tableau/widgets';

import { Messages } from '../../codegen/Localize';
import { CommonStyles } from '../../common/react/CommonStyles';
import { PageHeaderButtonClickEvent, TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { filePathDisplayStringWithBaseNameFirst } from '../../common/utilities/FilePathUtils';
import { imgPath } from '../../common/utilities/ImgUrl';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { isSameDataConnectionInfoProperties } from '../../ts/models/IDataConnectionInfo';
import { IDataSourceInfo } from '../../ts/models/IDataSourceInfo';
import { Telemetry } from '../../ts/services/TelemetryService';
import { HasClientLocation, mouseIsOutsideElement } from '../utils/RectangleUtils';
import { PageHeaderStyles } from './PageHeaderStyles';

export interface PageHeaderDataConnectionButtonProps {
  readonly url: string;
  readonly getDataSourceUrl: (s: string) => string;
  readonly getDataSources: () => ng.IPromise<IDataSourceInfo[]>;
  readonly displayOnlyIcons: boolean;
  readonly telemetryService: Telemetry;
}

export interface PageHeaderDataConnectionButtonState {
  readonly showingPanel: boolean;
  readonly dataConnections: null | CertificationWatermark.DataConnection[];
}

export class PageHeaderDataConnectionButton extends React.PureComponent<PageHeaderDataConnectionButtonProps, PageHeaderDataConnectionButtonState> {

  private buttonElement: HTMLElement;
  private panelElement: HTMLElement;
  private canceled: boolean;

  public constructor(props: PageHeaderDataConnectionButtonProps) {
    super(props);

    this.state = { showingPanel: false, dataConnections: null };
  }

  public render(): JSX.Element {

    return (
        <div style={{marginRight: this.props.displayOnlyIcons? '-2px' : '-1px', lineHeight: PageHeaderStyles.height}}>
          <ButtonWidget
            ref={this.setButtonRef}
            buttonType={ButtonType.IconOutline}
            getContent={this.buttonContent}
            title={this.isPanelOpen() ? Messages.pageHeaderDatasourcesClose() : Messages.pageHeaderDatasourcesOpen()}
            testId={'page-header-dataconnections'}
            handleClick={this.togglePanelVisibility}
            disabled={!this.state.dataConnections}
            style={{cursor: 'pointer'}}
          />
          {this.state.showingPanel && this.panel()}
        </div>
    );
  }

  private buttonContent = (interactionState: InteractionState): JSX.Element => {
    const showActiveIcon = (interactionState.active || interactionState.focus || interactionState.hover || interactionState.selected);
    const iconPath = showActiveIcon ? 'spaces_viz_view_header_ds_active.svg' : 'spaces_viz_view_header_ds.svg';

    return(
      <span className={PageHeaderStyles.TextButton}>
        <img src={imgPath(iconPath)} style={this.props.displayOnlyIcons ? PageHeaderStyles.iconOnlyButton : PageHeaderStyles.iconForTextButton} />
        {!this.props.displayOnlyIcons && Messages.pageHeaderDatasources()}
      </span>
    );
  }

  private setDataConnections = (dataConnections: CertificationWatermark.DataConnection[]) => {
    if (!this.canceled) {
      this.setState({ dataConnections });
    }
  }

  private toUniqueDataConnections = (dataSourceInfos: IDataSourceInfo[]): CertificationWatermark.DataConnection[] => {
    return dataSourceInfos.reduce(this.removeDuplicates, []).map(this.toDataConnection);
  }

  private toDataConnection = (dataSourceInfo: IDataSourceInfo): CertificationWatermark.DataConnection => {
    switch (dataSourceInfo.connectionDetails.type) {
      case 'file':
        return {
          name: dataSourceInfo.name,
          displayName: dataSourceInfo.name ||
            filePathDisplayStringWithBaseNameFirst(dataSourceInfo.connectionDetails.fileName),
          connectionType: 'File'
        };
      case 'tableauserver':
        return {
          name: dataSourceInfo.name,
          displayName: dataSourceInfo.connectionDetails.name,
          connectionType: 'TableauServer',
          url: this.props.getDataSourceUrl(dataSourceInfo.connectionDetails.id),
          isCertified: dataSourceInfo.connectionDetails.certificationDetails.isCertified
        };
      case 'tableauserversite':
        return {
          name: dataSourceInfo.name,
          displayName: dataSourceInfo.connectionDetails.siteUrl,
          connectionType: 'ExternalServer',
        };
      default:
        return {
          name: dataSourceInfo.name,
          displayName: dataSourceInfo.connectionTypeDisplayName,
          connectionType: 'ExternalServer',
          externalServer: dataSourceInfo.connectionDetails.serverName
        };
    }
  }

  private removeDuplicates(seen: IDataSourceInfo[], dataSourceInfo: IDataSourceInfo): IDataSourceInfo[] {
    return seen.some(isSameDataConnectionInfoProperties(dataSourceInfo)) ? seen : [ ...seen, dataSourceInfo ];
  }

  public componentDidMount(): void {
    this.canceled = false;
    if (FeatureFlags.dataConnectionTooltipInVizView) {
      this.props.getDataSources()
        .then(this.toUniqueDataConnections)
        .then(this.setDataConnections);
    }
  }

  // NOTE cancel any unresolved promise of content to avoid setting state on unmounted component,
  // see https://facebook.github.io/react/blog/2015/12/16/ismounted-antipattern.html for more information
  public componentWillUnmount(): void {
    this.canceled = true;
    this.stopWatchingClicks();
  }

  private setButtonRef = (element: ButtonWidget): void => {
    this.buttonElement = ReactDOM.findDOMNode(element) as HTMLElement;
  }

  private setPanelRef = (element: HTMLDivElement): void => {
    this.panelElement = ReactDOM.findDOMNode(element) as HTMLElement;
  }

  private togglePanelVisibility = () => {

    this.props.telemetryService.sendEvent<PageHeaderButtonClickEvent>({
      type: TelemetryEvents.PageHeaderButtonClick,
      button: 'data-connections-tooltip',
      action: this.state.showingPanel ? 'close' : 'open'
    });

    this.setState((prevState) => {
      return {showingPanel: !prevState.showingPanel};
    }, () => {
      if (this.state.showingPanel) {
        this.startWatchingClicks();
      } else {
        this.stopWatchingClicks();
      }
    });
  }

  private hidePanel = () => {
    this.setState({showingPanel: false});
    this.stopWatchingClicks();
  }

  private isPanelOpen() {
    return this.state.showingPanel;
  }

  private stopWatchingClicks = () => {
    document.removeEventListener('mousedown', this.onDocumentClick, true);
    document.removeEventListener('touchstart', this.onDocumentClick, true);
    document.removeEventListener('keyup', this.onKeyUp, true);
    window.removeEventListener('blur', this.onWindowBlur);
  }

  private startWatchingClicks = () => {
    document.addEventListener('mousedown', this.onDocumentClick, true);
    document.addEventListener('touchstart', this.onDocumentClick, true);
    document.addEventListener('keyup', this.onKeyUp, true);
    window.addEventListener('blur', this.onWindowBlur);
  }

  private onKeyUp = (evt: KeyboardEvent) => {
    if (evt.key === 'Escape' && this.buttonElement) {
      this.buttonElement.focus();
    }
  }

  private onDocumentClick = (evt: MouseEvent | TouchEvent) => {
    const clientPos = getClientPosition(evt);
    const hasClientLocation: HasClientLocation = {
      clientX: clientPos.x,
      clientY: clientPos.y
    };
    if (this.state.showingPanel && mouseIsOutsideElement(hasClientLocation, this.panelElement) && mouseIsOutsideElement(hasClientLocation, this.buttonElement)) {
      this.hidePanel();
    }
  }

  private onWindowBlur = () => {
    window.removeEventListener('blur', this.onWindowBlur);
    if (!this.canceled) {
      this.hidePanel();
    }
  }

  private panel = (): JSX.Element => {
    const props: DialogProps & React.HTMLAttributes<HTMLDivElement> = {
        anchorEl: this.buttonElement,
        testId: 'modal-dataconnections-pane',
        showCloseButton: false,
        type: DialogType.Uber,
        onCloseRequest: this.hidePanel,
        isModal: false,
        showFooter: false,
        shouldReturnFocusOnClose: true,
        positioningBehavior: DialogPositioningBehavior.Anchored,
        disableAutoFocus: false,
        dialogStyle: {padding: 0, boxShadow: 'none', borderWidth: '0px'},
        dialogContentStyle: {
          padding: 0,
          borderWidth: CommonStyles.Sizes.LineWidth,
          borderColor: CommonStyles.Colors.BorderColor,
          borderStyle: 'solid'
        },
        title: Messages.dataConnectionDialogTitle(),
        dialogBodyStyle: {paddingTop: 0, paddingBottom: 0, paddingLeft: 0, paddingRight: 0}
    };

    const contentProps: CertificationWatermark.DataConnectionTooltipProps = {
      label: Messages.pageHeaderDatasources(),
      url: this.props.url,
      dataConnections: this.state.dataConnections
    };

    return (
      <DialogWidget {...props}>
        <div ref={this.setPanelRef} className={PageHeaderStyles.dataConnectionsTooltipClass}>
          <CertificationWatermark.DataConnectionTooltipComponentContent {...contentProps}/>
        </div>
      </DialogWidget>
    );
  }
}
